import axios from "axios"
import { useEffect, useMemo, useRef, useState } from "react"
import Button from "../../components/Button/Button.jsx"
import Footer from "../../components/Footer/Footer.jsx"
import { submitApplicationRequest } from "../../api/requestsApi.js"
import "../../components/Form/Form.css"
import "../../components/Form/ModalForm.css"
import { CalculatorSkeleton } from "./ProductSkeletons"
import "./Product.css"

const licenseModes = [
    { id: "basic", name: "Базовая", description: "Без дополнительного функционала" },
    { id: "standard", name: "Стандарт", description: "Включен стандартный функционал" },
    { id: "premium", name: "Премиум", description: "Весь функционал приложения" },
    { id: "custom", name: "Кастомная", description: "Ручная настройка доп. функционала лицензии" },
]

const durationOptions = [
    { id: "custom", name: "До года", description: "От 2 месяцев до 1 года" },
    { id: "two_years", name: "2 года", description: "Цена со скидкой" },
    { id: "perpetual", name: "Бессрочная", description: "Стоимость за неограниченный период" },
]

const supportOptions = [
    {
        id: "standard",
        name: "Техническая поддержка",
        settingsKey: "standard_support_percent",
        description: "Консультации, помощь с настройкой, разбор инцидентов и сопровождение обновлений в рабочее время.",
    },
]

const implementationOptions = [
    { id: "setup", name: "Установка и настройка", settingsKey: "setup_implementation" },
]

const defaultSettings = {
    standard_support_percent: 15,
    priority_support_percent: 25,
    setup_implementation: 50000,
    two_year_discount_percent: 10,
    perpetual_license_percent: 300,
    vat_percent: 22,
}

const MAX_LICENSES_COUNT = 10000000000

function isSellableProduct(product) {
    return !product?.hidden && !product?.info_only
}

function formatPrice(value) {
    return new Intl.NumberFormat("ru-RU").format(value)
}

function formatRubles(value) {
    return `${formatPrice(value)}\u00A0₽`
}

function formatMonths(value) {
    const months = Number(value)

    if (months === 12) return "1 год"
    if (months === 24) return "2 года"
    if (months === 36) return "3 года"

    const mod10 = months % 10
    const mod100 = months % 100
    const suffix = mod10 === 1 && mod100 !== 11
        ? "месяц"
        : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
            ? "месяца"
            : "месяцев"

    return `${months} ${suffix}`
}

function formatDurationShort(durationId, durationMonths, twoYearDiscountPercent) {
    if (durationId === "perpetual") {
        return "бессрочно"
    }

    if (durationId === "two_years") {
        return `${durationMonths} мес. со скидкой ${twoYearDiscountPercent}%`
    }

    return `${durationMonths} мес.`
}

function getFeatureKeysForMode(product, mode) {
    const features = product?.features ?? []

    if (mode === "basic") return []
    if (mode === "standard") return sanitizeFeatureKeys(product, features.filter((feature) => feature.include_standard).map((feature) => feature.key))
    if (mode === "premium") return sanitizeFeatureKeys(product, features.map((feature) => feature.key))

    return []
}

function sanitizeFeatureKeys(product, featureKeys) {
    const features = product?.features ?? []
    const existingKeys = new Set(features.map((feature) => feature.key))
    const selectedKeys = new Set(featureKeys.filter((key) => existingKeys.has(key)))
    let changed = true

    while (changed) {
        changed = false

        for (const feature of features) {
            if (
                selectedKeys.has(feature.key)
                && feature.required_feature_id
                && !selectedKeys.has(feature.required_feature_id)
            ) {
                selectedKeys.delete(feature.key)
                changed = true
            }
        }
    }

    return features
        .filter((feature) => selectedKeys.has(feature.key))
        .map((feature) => feature.key)
}

function createProductConfig(product, mode = "standard") {
    return {
        licenseMode: mode,
        licensesCount: 50,
        selectedFeatureKeys: getFeatureKeysForMode(product, mode),
    }
}

function CalculatorGroupHeading({ title, hint }) {
    const normalizedHint = hint.trim()

    return (
        <div className="calculator__groupHeader">
            <h3>{title}</h3>
            {normalizedHint !== "" && (
                <div className="calculator__hintBox">
                    <button
                        className="calculator__hintButton"
                        type="button"
                        aria-label={`Показать подсказку для блока ${title}`}
                    >
                        ?
                    </button>
                    <div className="calculator__hintPopover" role="note">
                        {normalizedHint}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function CalculatorPage() {
    const calculatorRef = useRef(null)
    const summarySlotRef = useRef(null)
    const summaryRef = useRef(null)
    const [products, setProducts] = useState([])
    const [settings, setSettings] = useState(defaultSettings)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedProductKeys, setSelectedProductKeys] = useState([])
    const [configsByProduct, setConfigsByProduct] = useState({})
    const [durationId, setDurationId] = useState("custom")
    const [customDurationMonths, setCustomDurationMonths] = useState(12)
    const [isSupportEnabled, setIsSupportEnabled] = useState(true)
    const [supportOptionId, setSupportOptionId] = useState("standard")
    const [isImplementationEnabled, setIsImplementationEnabled] = useState(true)
    const [implementationOptionId, setImplementationOptionId] = useState("setup")
    const [isPurchaseOpen, setIsPurchaseOpen] = useState(false)
    const [purchaseEmail, setPurchaseEmail] = useState("")
    const [purchaseOrganization, setPurchaseOrganization] = useState("")
    const [purchaseComment, setPurchaseComment] = useState("")
    const [purchaseAgree, setPurchaseAgree] = useState(false)
    const [purchaseSending, setPurchaseSending] = useState(false)
    const [purchaseStatus, setPurchaseStatus] = useState("")
    const [purchaseError, setPurchaseError] = useState("")
    const [isSummaryFixed, setIsSummaryFixed] = useState(false)
    const [summaryFixedStyle, setSummaryFixedStyle] = useState({})
    useEffect(() => {
        async function fetchData() {
            try {
                const [productsResponse, settingsResponse] = await Promise.all([
                    axios.get("/api/products"),
                    axios.get("/api/calculator-settings"),
                ])
                const loadedProducts = (productsResponse.data.data ?? []).filter(isSellableProduct)

                setProducts(loadedProducts)
                setSettings(settingsResponse.data.data ?? defaultSettings)

                if (loadedProducts[0]) {
                    setSelectedProductKeys([loadedProducts[0].key])
                    setConfigsByProduct({
                        [loadedProducts[0].key]: createProductConfig(loadedProducts[0]),
                    })
                }
            } catch (error) {
                console.error("Ошибка при загрузке калькулятора", error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [])

    useEffect(() => {
        let animationFrame = 0

        function updateSummaryPosition() {
            cancelAnimationFrame(animationFrame)
            animationFrame = requestAnimationFrame(() => {
                const summarySlot = summarySlotRef.current
                const summary = summaryRef.current

                if (!summarySlot || !summary || window.innerWidth < 900) {
                    setIsSummaryFixed(false)
                    setSummaryFixedStyle({})
                    return
                }

                const slotRect = summarySlot.getBoundingClientRect()
                const summaryHeight = summary.offsetHeight
                const headerOffset = 104
                const maxOffset = Math.max(0, summarySlot.offsetHeight - summaryHeight)
                const nextOffset = Math.min(maxOffset, Math.max(0, headerOffset - slotRect.top))

                setIsSummaryFixed(nextOffset > 0)
                setSummaryFixedStyle({
                    transform: `translate3d(0, ${Math.round(nextOffset)}px, 0)`,
                })
            })
        }

        updateSummaryPosition()
        window.addEventListener("scroll", updateSummaryPosition, { passive: true })
        window.addEventListener("resize", updateSummaryPosition)

        const resizeObserver = new ResizeObserver(updateSummaryPosition)
        if (summarySlotRef.current) resizeObserver.observe(summarySlotRef.current)
        if (summaryRef.current) resizeObserver.observe(summaryRef.current)

        return () => {
            cancelAnimationFrame(animationFrame)
            window.removeEventListener("scroll", updateSummaryPosition)
            window.removeEventListener("resize", updateSummaryPosition)
            resizeObserver.disconnect()
        }
    }, [])

    const selectedProducts = useMemo(
        () => products.filter((product) => selectedProductKeys.includes(product.key)),
        [products, selectedProductKeys]
    )

    const supportOption = supportOptions.find((item) => item.id === supportOptionId) ?? supportOptions[0]
    const implementationOption = implementationOptions.find((item) => item.id === implementationOptionId) ?? implementationOptions[0]
    const hasSelectedProducts = selectedProducts.length > 0
    const supportPercent = isSupportEnabled && hasSelectedProducts ? Number(settings[supportOption.settingsKey] ?? 0) : 0
    const implementationPrice = isImplementationEnabled && hasSelectedProducts ? Number(settings[implementationOption.settingsKey] ?? 0) : 0
    const twoYearDiscountPercent = Number(settings.two_year_discount_percent ?? 0)
    const vatPercent = Number(settings.vat_percent ?? 0)
    const durationMonths = durationId === "perpetual"
        ? 12 * (Number(settings.perpetual_license_percent ?? 100) / 100)
        : durationId === "two_years"
            ? 24
            : customDurationMonths
    const durationDiscountMultiplier = durationId === "two_years"
        ? 1 - twoYearDiscountPercent / 100
        : 1
    const durationLabel = durationId === "perpetual"
        ? "Бессрочная"
        : durationId === "two_years"
            ? `2 года, скидка ${twoYearDiscountPercent}%`
            : formatMonths(customDurationMonths)
    const durationShortLabel = formatDurationShort(durationId, durationMonths, twoYearDiscountPercent)

    const productTotals = selectedProducts.map((product) => {
        const config = configsByProduct[product.key] ?? createProductConfig(product)
        const selectedFeatureKeys = sanitizeFeatureKeys(product, config.selectedFeatureKeys)
        const selectedFeatures = (product.features ?? []).filter((feature) => selectedFeatureKeys.includes(feature.key))
        const featurePrice = selectedFeatures.reduce((sum, feature) => sum + Number(feature.price), 0)
        const monthlyPricePerLicense = Number(product.price) + featurePrice
        const pricePerLicense = Math.round(monthlyPricePerLicense * durationMonths * durationDiscountMultiplier)
        const licensesCount = Number(config.licensesCount ?? 1)
        const subtotal = pricePerLicense * licensesCount

        return {
            product,
            config,
            selectedFeatures,
            pricePerLicense,
            licensesCount,
            subtotal,
        }
    })

    const licenseSubtotal = productTotals.reduce((sum, item) => sum + item.subtotal, 0)
    const supportBase = licenseSubtotal
    const supportTotal = Math.round(supportBase * (supportPercent / 100))
    const licenseVat = Math.round(licenseSubtotal * (vatPercent / 100))
    const licenseTotalWithVat = licenseSubtotal + licenseVat
    const servicesSubtotal = supportTotal + implementationPrice
    const servicesVat = Math.round(servicesSubtotal * (vatPercent / 100))
    const servicesTotalWithVat = servicesSubtotal + servicesVat
    const total = licenseTotalWithVat + servicesTotalWithVat
    const isValidPurchaseEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(purchaseEmail.trim())
    const isPurchaseDisabled = !isValidPurchaseEmail || purchaseOrganization.trim() === "" || !purchaseAgree || purchaseSending || productTotals.length === 0

    const toggleProduct = (product) => {
        setSelectedProductKeys((current) => {
            if (current.includes(product.key)) {
                return current.filter((key) => key !== product.key)
            }

            return [...current, product.key]
        })

        setConfigsByProduct((current) => ({
            ...current,
            [product.key]: current[product.key] ?? createProductConfig(product),
        }))
    }

    const updateProductConfig = (productKey, patch) => {
        setConfigsByProduct((current) => ({
            ...current,
            [productKey]: {
                ...current[productKey],
                ...patch,
            },
        }))
    }

    const handleLicenseModeChange = (product, mode) => {
        updateProductConfig(product.key, {
            licenseMode: mode,
            selectedFeatureKeys: mode === "custom"
                ? configsByProduct[product.key]?.selectedFeatureKeys ?? []
                : getFeatureKeysForMode(product, mode),
        })
    }

    const toggleFeature = (product, featureKey) => {
        const config = configsByProduct[product.key] ?? createProductConfig(product)
        const feature = (product.features ?? []).find((item) => item.key === featureKey)
        const selectedFeatureKeys = sanitizeFeatureKeys(product, config.selectedFeatureKeys)

        if (
            !selectedFeatureKeys.includes(featureKey)
            && feature?.required_feature_id
            && !selectedFeatureKeys.includes(feature.required_feature_id)
        ) {
            return
        }

        updateProductConfig(product.key, {
            licenseMode: "custom",
            selectedFeatureKeys: sanitizeFeatureKeys(
                product,
                selectedFeatureKeys.includes(featureKey)
                    ? selectedFeatureKeys.filter((key) => key !== featureKey)
                    : [...selectedFeatureKeys, featureKey]
            ),
        })
    }

    const changeLicensesCount = (productKey, value) => {
        updateProductConfig(productKey, {
            licensesCount: Math.min(MAX_LICENSES_COUNT, Math.max(1, Number(value) || 1)),
        })
    }

    const buildPurchaseMessage = () => {
        const productLines = productTotals.map(({ product, config, selectedFeatures, pricePerLicense, licensesCount, subtotal }) => {
            const mode = licenseModes.find((item) => item.id === config.licenseMode)?.name ?? config.licenseMode
            const featureList = selectedFeatures.length > 0
                ? selectedFeatures.map((feature) => feature.name).join(", ")
                : "без дополнительных фич"
            const monthlyPrice = Number(product.price) + selectedFeatures.reduce((sum, feature) => sum + Number(feature.price), 0)

            return [
                `${product.module}`,
                `Лицензия: ${mode}`,
                `Фичи: ${featureList}`,
                `Количество лицензий: ${licensesCount}`,
                `Цена за 1 лицензию в месяц: ${formatRubles(monthlyPrice)}`,
                `Формула: ${formatPrice(monthlyPrice)} ₽/мес × ${durationShortLabel} × ${licensesCount} = ${formatRubles(subtotal)}`,
            ].join("\n")
        }).join("\n\n")

        return [
            "Заявка на покупку из калькулятора",
            "",
            productLines,
            "",
            `Срок лицензии: ${durationLabel}`,
            `Лицензии без НДС: ${formatRubles(licenseSubtotal)}`,
            `НДС ${vatPercent}% на лицензии: ${formatRubles(licenseVat)}`,
            `Итого по лицензиям с НДС: ${formatRubles(licenseTotalWithVat)}`,
            isSupportEnabled
                ? `Техподдержка: ${supportOption.name} (${supportPercent}% только от лицензий) = ${formatRubles(supportTotal)}`
                : "Техподдержка: не требуется",
            isImplementationEnabled
                ? `Внедрение: ${implementationOption.name} = ${formatRubles(implementationPrice)}`
                : "Внедрение: не требуется",
            `НДС ${vatPercent}% на услуги: ${formatRubles(servicesVat)}`,
            `Итого по услугам с НДС: ${formatRubles(servicesTotalWithVat)}`,
            `Итого: ${formatRubles(total)}`,
            "",
            purchaseComment.trim() ? `Комментарий клиента: ${purchaseComment.trim()}` : "",
        ].filter(Boolean).join("\n")
    }

    const handlePurchaseSubmit = async (event) => {
        event.preventDefault()

        if (isPurchaseDisabled) return

        setPurchaseSending(true)
        setPurchaseStatus("")
        setPurchaseError("")

        try {
            await submitApplicationRequest({
                type: "Заявка на покупку",
                email: purchaseEmail.trim(),
                organization: purchaseOrganization.trim(),
                message: buildPurchaseMessage(),
            })

            setPurchaseStatus("Заявка отправлена")
            setPurchaseEmail("")
            setPurchaseOrganization("")
            setPurchaseComment("")
            setPurchaseAgree(false)
        } catch (error) {
            setPurchaseError(error?.message || "Ошибка отправки")
        } finally {
            setPurchaseSending(false)
        }
    }

    return (
        <>
            <main className="product">
                <section className="product__header product__header_calculator">
                    <h1>Калькулятор стоимости</h1>
                </section>

                {isLoading && <CalculatorSkeleton />}

                {!isLoading && products.length === 0 && (
                    <p className="product__message">Продукты пока не добавлены.</p>
                )}

                {!isLoading && products.length > 0 && (
                    <section className="calculator" ref={calculatorRef}>
                        <div className="calculator__config">
                            <div className="calculator__group calculator__sectionCard calculator__sectionCard_ecosystem">
                                <h2>Продукты экосистемы</h2>
                                <div className="calculator__products">
                                    {products.map((item) => (
                                        <label className="calculator__check" key={item.key}>
                                            <input
                                                type="checkbox"
                                                checked={selectedProductKeys.includes(item.key)}
                                                onChange={() => toggleProduct(item)}
                                            />
                                            <span>
                                            <b>{item.module}</b>
                                            <small>{formatPrice(item.price)} ₽ / лицензия</small>
                                        </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {selectedProducts.length === 0 && (
                                <p className="product__message">Выберите хотя бы один продукт.</p>
                            )}

                            {productTotals.map(({ product, config }) => (
                                <section className="calculator__productConfig" key={product.key}>
                                    <div className="calculator__productHeader">
                                        <h2>{product.module}</h2>
                                    </div>

                                    <label className="calculator__number calculator__number_highlight">
                                        <span>Количество пользователей для {product.module}</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={MAX_LICENSES_COUNT}
                                            value={config.licensesCount}
                                            onChange={(event) => changeLicensesCount(product.key, event.target.value)}
                                        />
                                    </label>

                                    <div className="calculator__group calculator__sectionCard">
                                        <CalculatorGroupHeading
                                            title="Лицензия"
                                            hint={product.license_hint ?? ""}
                                        />
                                        <h3>Лицензия</h3>
                                        <div className="calculator__options">
                                            {licenseModes.map((item) => (
                                                <button
                                                    className={config.licenseMode === item.id ? "calculator__option calculator__option_active" : "calculator__option"}
                                                    type="button"
                                                    key={item.id}
                                                    onClick={() => handleLicenseModeChange(product, item.id)}
                                                >
                                                    <span>{item.name}</span>
                                                    <small>{item.description}</small>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="calculator__group calculator__sectionCard">
                                        <CalculatorGroupHeading
                                            title="Фичи лицензии"
                                            hint={product.features_hint ?? ""}
                                        />
                                        <h3>Фичи лицензии</h3>
                                        {(product.features ?? []).length === 0 && (
                                            <p className="product__message">Для продукта пока не добавлены фичи.</p>
                                        )}
                                        {(product.features ?? []).length > 0 && (
                                            <div className="calculator__products">
                                                {product.features.map((feature) => {
                                                    const selectedFeatureKeys = sanitizeFeatureKeys(product, config.selectedFeatureKeys)
                                                    const requiredFeature = (product.features ?? []).find((item) => item.key === feature.required_feature_id)
                                                    const isFeatureLocked = Boolean(requiredFeature && !selectedFeatureKeys.includes(requiredFeature.key))

                                                    return (
                                                        <label
                                                            className={isFeatureLocked ? "calculator__check calculator__check_disabled" : "calculator__check"}
                                                            key={feature.key}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedFeatureKeys.includes(feature.key)}
                                                                onChange={() => toggleFeature(product, feature.key)}
                                                                disabled={isFeatureLocked}
                                                            />
                                                            <span>
                                                            <b>{feature.name}</b>
                                                            <small>+ {formatPrice(feature.price)} ₽ / лицензия</small>
                                                                {isFeatureLocked && <small>Требуется: {requiredFeature.name}</small>}
                                                        </span>
                                                        </label>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ))}

                            <div className="calculator__group calculator__sectionCard">
                                <h3>Срок лицензии</h3>
                                <div className="calculator__options">
                                    {durationOptions.map((item) => (
                                        <button
                                            className={durationId === item.id ? "calculator__option calculator__option_active" : "calculator__option"}
                                            type="button"
                                            key={item.id}
                                            onClick={() => setDurationId(item.id)}
                                            disabled={!hasSelectedProducts}
                                        >
                                            <span>{item.name}</span>
                                            <small>
                                                {item.id === "custom"
                                                    ? formatMonths(customDurationMonths)
                                                    : item.id === "two_years"
                                                        ? `Скидка ${twoYearDiscountPercent}%`
                                                        : item.id === "perpetual"
                                                            ? `${settings.perpetual_license_percent}% от годовой`
                                                            : item.description}
                                            </small>
                                        </button>
                                    ))}
                                </div>
                                {durationId === "custom" && (
                                    <label className="calculator__range">
                                        <span>{formatMonths(customDurationMonths)}</span>
                                        <input
                                            type="range"
                                            min="2"
                                            max="12"
                                            step="1"
                                            value={customDurationMonths}
                                            onChange={(event) => setCustomDurationMonths(Number(event.target.value))}
                                            disabled={!hasSelectedProducts}
                                        />
                                        <small>2 месяца - 1 год</small>
                                    </label>
                                )}
                            </div>

                            <div className="calculator__group calculator__sectionCard">
                                <h3>Техническая поддержка</h3>
                                <label className="calculator__toggle">
                                    <input
                                        type="checkbox"
                                        checked={isSupportEnabled}
                                        onChange={(event) => setIsSupportEnabled(event.target.checked)}
                                        disabled={!hasSelectedProducts}
                                    />
                                    <span>Использовать техподдержку</span>
                                </label>
                                <div className="calculator__options">
                                    {supportOptions.map((item) => (
                                        <button
                                            className={supportOptionId === item.id ? "calculator__option calculator__option_active" : "calculator__option"}
                                            type="button"
                                            key={item.id}
                                            onClick={() => setSupportOptionId(item.id)}
                                            disabled={!isSupportEnabled || !hasSelectedProducts}
                                        >
                                            <span>{item.name}</span>
                                            <small>{settings[item.settingsKey]}% только от стоимости лицензий</small>
                                            <small>{item.description}</small>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="calculator__group calculator__sectionCard">
                                <h3>Внедрение</h3>
                                <label className="calculator__toggle">
                                    <input
                                        type="checkbox"
                                        checked={isImplementationEnabled}
                                        onChange={(event) => setIsImplementationEnabled(event.target.checked)}
                                        disabled={!hasSelectedProducts}
                                    />
                                    <span>Использовать внедрение</span>
                                </label>
                                {!isImplementationEnabled && hasSelectedProducts && (
                                    <p className="calculator__hint">
                                        Мы не предоставляем облачное размещение. Без услуги внедрения вы получите
                                        дистрибутив и документацию по самостоятельной установке. Внедрение оплачивается
                                        один раз.
                                    </p>
                                )}
                                <div className="calculator__options">
                                    {implementationOptions.map((item) => (
                                        <button
                                            className={implementationOptionId === item.id ? "calculator__option calculator__option_active" : "calculator__option"}
                                            type="button"
                                            key={item.id}
                                            onClick={() => setImplementationOptionId(item.id)}
                                            disabled={!isImplementationEnabled || !hasSelectedProducts}
                                        >
                                            <span>{item.name}</span>
                                            <small>{formatPrice(settings[item.settingsKey])} ₽</small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="calculator__summarySlot" ref={summarySlotRef}>
                            <aside
                                ref={summaryRef}
                                className={isSummaryFixed ? "calculator__summary calculator__summary_fixed" : "calculator__summary"}
                                style={summaryFixedStyle}
                            >
                                <h2>Итого с НДС</h2>
                                <div className="calculator__summaryDivider" />
                                <section className="calculator__summarySection">
                                    <h3>Лицензии</h3>
                                </section>
                                <dl className="calculator__summaryList">
                                    {productTotals.map(({ product, selectedFeatures, licensesCount, subtotal }) => {
                                        const monthlyPrice = Number(product.price) + selectedFeatures.reduce((sum, feature) => sum + Number(feature.price), 0)

                                        return (
                                            <div className="calculator__summaryProductRow" key={product.key}>
                                                <dt>{product.module}</dt>
                                                <dd className="calculator__summaryFormula">
                                                    <span>{formatPrice(monthlyPrice)} ₽/мес</span>
                                                    <span>× {durationShortLabel} × {licensesCount}</span>
                                                    <strong>= {formatRubles(subtotal)}</strong>
                                                </dd>
                                            </div>
                                        )
                                    })}
                                    <div>
                                        <dt>НДС {vatPercent}%</dt>
                                        <dd>{formatRubles(licenseVat)}</dd>
                                    </div>
                                    <div>
                                        <dt>Итого с НДС</dt>
                                        <dd>{formatRubles(licenseTotalWithVat)}</dd>
                                    </div>
                                    <div>
                                        <dt>Срок лицензии</dt>
                                        <dd>{durationLabel}</dd>
                                    </div>
                                </dl>
                                <div className="calculator__summaryDivider" />
                                <section className="calculator__summaryServices">
                                    <h3>Услуги</h3>
                                    <dl className="calculator__summaryList">
                                        <div>
                                            <dt>Техподдержка</dt>
                                            <dd>{isSupportEnabled ? formatRubles(supportTotal) : "Не требуется"}</dd>
                                        </div>
                                        <div>
                                            <dt>Внедрение</dt>
                                            <dd>{isImplementationEnabled ? formatRubles(implementationPrice) : "Не требуется"}</dd>
                                        </div>
                                        <div>
                                            <dt>НДС {vatPercent}%</dt>
                                            <dd>{formatRubles(servicesVat)}</dd>
                                        </div>
                                        <div>
                                            <dt>Итого с НДС</dt>
                                            <dd>{formatRubles(servicesTotalWithVat)}</dd>
                                        </div>
                                    </dl>
                                </section>
                                <div className="calculator__summaryTotal">
                                    <strong>{formatRubles(total)}*</strong>
                                    <span>Не является публичной офертой</span>
                                </div>
                                <button
                                    className="calculator__buyButton"
                                    type="button"
                                    onClick={() => setIsPurchaseOpen(true)}
                                    disabled={productTotals.length === 0}
                                >
                                    Купить
                                </button>
                            </aside>
                        </div>
                    </section>
                )}

                {isPurchaseOpen && (
                    <div className="modal_form_overlay" role="dialog" aria-modal="true" onClick={() => setIsPurchaseOpen(false)}>
                        <div className="modal_form_content purchaseModal__content" onClick={(event) => event.stopPropagation()}>
                            <button className="modal_form_close" type="button" onClick={() => setIsPurchaseOpen(false)}>
                                ×
                            </button>
                            <div className="form_container purchaseModal__formContainer">
                                <div className="purchaseModal__heading">
                                    <h2>Заявка на покупку</h2>
                                </div>

                                {purchaseError && <div className="form_status form_status_error">Ошибка: {purchaseError}</div>}
                                {purchaseStatus && <div className="form_status form_status_success">{purchaseStatus}</div>}

                                <form className="form__main" onSubmit={handlePurchaseSubmit}>
                                    <div className="form_field">
                                        <input
                                            type="email"
                                            value={purchaseEmail}
                                            onChange={(event) => setPurchaseEmail(event.target.value)}
                                            placeholder="e-mail"
                                            required
                                            disabled={purchaseSending}
                                        />
                                    </div>

                                    <div className="form_field">
                                        <input
                                            type="text"
                                            value={purchaseOrganization}
                                            onChange={(event) => setPurchaseOrganization(event.target.value)}
                                            placeholder="Организация"
                                            required
                                            disabled={purchaseSending}
                                        />
                                    </div>

                                    <div className="form_field">
                                    <textarea
                                        value={purchaseComment}
                                        onChange={(event) => setPurchaseComment(event.target.value)}
                                        placeholder="Комментарий к заявке"
                                        rows={4}
                                        disabled={purchaseSending}
                                    />
                                    </div>

                                    <label className="form_checkbox">
                                        <input
                                            type="checkbox"
                                            checked={purchaseAgree}
                                            onChange={(event) => setPurchaseAgree(event.target.checked)}
                                            disabled={purchaseSending}
                                        />
                                        <span>Я принимаю <a href="/privacy">политику обработки персональных данных</a></span>
                                    </label>

                                    <div className="form_actions">
                                        <Button typeButton="submit" disabled={isPurchaseDisabled}>
                                            {purchaseSending ? "Отправка..." : "Отправить заявку"}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    )
}
